/**
 * Responsibility: describe the canyon country biome — a high plateau cut by deep winding gorges.
 *
 * ## The generative trick: the absolute value of a noise field
 *
 * A canyon is a *line* feature, and lines are hard to get out of noise. Thresholding a field gives blobs; a canyon
 * needs a winding, branching, continuous channel.
 *
 * `Math.abs(field)` solves it. The absolute value is near zero along the field's **zero-crossing contours**, and the
 * zero set of a 2D noise field is exactly a family of winding, branching, continuous curves. So "close to zero"
 * means "inside a canyon", and a whole drainage network costs one noise sample.
 *
 * Crucially it is evaluable at any coordinate independently — no path tracing, no accumulated state — which is what
 * determinism requires and what a corridor system would otherwise have to work hard for.
 *
 * Channel width comes from the same maths for free: the world-space width is the threshold divided by the local
 * gradient, so the network narrows where the field changes quickly and opens into basins where it is flat.
 *
 * ## Cut down from a raised rim
 *
 * The rim sits high and the canyon is subtracted from it, so the floor never goes below zero. Negative terrain is
 * possible but would cost a palette change and would drag neighbouring biomes down through zero across a transition
 * thousands of units wide. Raising the rim is free.
 *
 * ## Terraced walls
 *
 * Depth is quantised in steps, exactly as the mesa biome quantises height, so walls come out benched rather than as
 * smooth funnels. That is what canyon walls look like, and it makes the two rock biomes read as related country.
 *
 * Contains no Three.js and no DOM access, so it is fully unit testable.
 */

import { createFbm, toUnitRange } from '../noise.js';
import { createRockFormations } from '../rockFormations.js';
import { CANYON, PALETTE } from '../../config.js';

export const canyon = {
  id: 'canyon',
  label: 'Canyon country',

  palette: PALETTE.canyon,

  suitabilityBias: CANYON.SUITABILITY_BIAS,

  /**
   * Build the elevation function for this biome.
   *
   * @param {number} seed - Base world seed.
   * @returns {(x: number, z: number) => number} Elevation in world units, always >= 0.
   */
  createElevation(seed) {
    /*
      The channel field is used *signed*, unlike every other biome's, because it is its zero crossing that matters
      rather than its magnitude.
    */
    const channel = createFbm(seed, 'channel:canyon', {
      octaves: CANYON.CHANNEL_OCTAVES,
      baseFrequency: 1 / CANYON.CHANNEL_WAVELENGTH,
      persistence: CANYON.CHANNEL_PERSISTENCE,
      lacunarity: CANYON.LACUNARITY,
    });

    /*
      A separate field for the plateau's undulation, so the rim does not rise exactly where the canyons run. Sharing
      one would tie the two together and they would read as a single feature.
    */
    const relief = createFbm(seed, 'rim:canyon', {
      octaves: 2,
      baseFrequency: 1 / CANYON.RIM_WAVELENGTH,
      persistence: 0.4,
      lacunarity: CANYON.LACUNARITY,
    });

    return function sampleCanyonElevation(x, z) {
      const rim = CANYON.RIM_HEIGHT + toUnitRange(relief(x, z)) * CANYON.RIM_RELIEF;

      /*
        Distance from the canyon's centre line, measured in field value rather than world units: 0 on the centre
        line, 1 at the rim, and everything beyond is untouched plateau.
      */
      const fromCentre = Math.abs(channel(x, z)) / CANYON.CHANNEL_WIDTH;
      if (fromCentre >= 1) return rim;

      /*
        `WALL_SHARPNESS` above 1 holds the floor near full depth and then climbs steeply, which gives a flat-bottomed
        gorge with steep walls rather than a V or a funnel.
      */
      const rawDepth = CANYON.DEPTH * (1 - Math.pow(fromCentre, CANYON.WALL_SHARPNESS));

      /*
        Quantised into benches, the way the mesa biome quantises height. A smooth profile reads as a moulded trench;
        stepping it reads as rock.
      */
      /*
        Rounded, not floored. Flooring cost most of the canyon's depth: the centre line reaches nearly `DEPTH` but
        never exactly, so a 319-unit incision floored to 240 and the deepest bench was unreachable. Measured, the
        deepest floor was 142 units below a 380 rim instead of the configured 320.
      */
      const benched =
        CANYON.STEP_HEIGHT > 0
          ? Math.round(rawDepth / CANYON.STEP_HEIGHT) * CANYON.STEP_HEIGHT
          : rawDepth;

      // Never negative: the floor is a hard limit, and RIM_HEIGHT is configured to exceed DEPTH anyway.
      return Math.max(0, rim - benched);
    };
  },

  /** Highest elevation this biome can produce, in world units — the rim, since canyons only ever cut down. */
  maxElevation: CANYON.RIM_HEIGHT + CANYON.RIM_RELIEF,

  /**
   * Build the structure placement function: arches, spires and hoodoos on the plateau and the canyon floors.
   *
   * Shared with mesa country, which is the same geology. Their own slope rules keep them off the walls, so they
   * stand on level ground at the top and bottom of a gorge rather than clinging to its sides. An arch is held to a
   * tighter limit still, because it has to plant two feet hundreds of units apart.
   *
   * @param {number} seed - Base world seed.
   * @returns {(region: object, context: object) => Array<object>}
   */
  createStructures(seed) {
    return createRockFormations(seed, 'canyon');
  },
};
